import { Analysis, PossibleRootCauseCategory, ProbabilityCategory } from '.';

export class GlucoseAnalysis extends Analysis {
    glucoseRegulationProbability?: number;
    glucoseRegulationProbabilityCategory?: ProbabilityCategory;

    reset() {
        super.reset();
        this.glucoseRegulationProbability = -1;
        this.glucoseRegulationProbabilityCategory = ProbabilityCategory.NA;
    }

    performAnalysis() {
        super.performAnalysis();
        this.calculateGlucoseRegulationLikelihood();
        this.calculateGlucoseRegulationLikelihoodCategory();
    }

    calculateGlucoseRegulationLikelihood() {
        const ba = this.ba;
        const hasGlucose = ba.glucose?.hasValue?.();
        const hasHbA1c   = ba.hemoglobinA1c?.hasValue?.();
        const hasTgHdl   = ba.triglyceridesHDLRatio?.hasValue?.();
        const hasInsulin = ba.insulin?.hasValue?.();
        const hasCp      = ba.cpeptide?.hasValue?.();

        // Bail only when no glucose-related marker at all is present. Insulin
        // and C-peptide qualify now that the gauge surfaces an insulin-signal
        // tier (the early-warning insulin-resistance use case where Glucose
        // and HbA1c sit in range but Insulin is elevated).
        if (!hasGlucose && !hasHbA1c && !hasTgHdl && !hasInsulin && !hasCp) return;

        // Alarm boundary checks use >= alarmMax / <= alarmMin (inclusive at the
        // boundary), unlike the existing isGreaterThanAlarm/isLessThanAlarm
        // helpers which are strict. A Glucose value exactly equal to alarmMax
        // is treated as alarm-tier, not lab-tier — confirmed product decision.
        const geAlarmHigh = (m?: any): boolean =>
            !!m?.hasValue?.() && !!m?.range && m.value >= m.range.alarmMax;
        const leAlarmLow = (m?: any): boolean =>
            !!m?.hasValue?.() && !!m?.range && m.value <= m.range.alarmMin;

        const gLab    = hasGlucose && ba.glucose.isGreaterThanLab();
        const gLowLab = hasGlucose && ba.glucose.isLessThanLab();
        const gAlarm  = geAlarmHigh(ba.glucose);
        const gAlarmLow = leAlarmLow(ba.glucose);
        const gHigh   = hasGlucose && ba.glucose.isGreaterThanOptimal();
        const gLow    = hasGlucose && ba.glucose.isStricklyLessThanOptimal();

        const hLab    = hasHbA1c && ba.hemoglobinA1c.isGreaterThanLab();
        const hLowLab = hasHbA1c && ba.hemoglobinA1c.isLessThanLab();
        const hAlarm  = geAlarmHigh(ba.hemoglobinA1c);
        const hAlarmLow = leAlarmLow(ba.hemoglobinA1c);
        const hHigh   = hasHbA1c && ba.hemoglobinA1c.isGreaterThanOptimal();
        const hLow    = hasHbA1c && ba.hemoglobinA1c.isStricklyLessThanOptimal();

        const tLab   = hasTgHdl && ba.triglyceridesHDLRatio.isGreaterThanLab();
        const tAlarm = geAlarmHigh(ba.triglyceridesHDLRatio);
        const tHigh  = hasTgHdl && ba.triglyceridesHDLRatio.isGreaterThanOptimal();

        // Insulin and C-peptide collapse into a single "insulin-signal" per
        // Q2Y — both reflect endogenous insulin output, so the gauge counts
        // whichever is more extreme and surfaces a combined category rather
        // than two near-duplicate rows. Multi-marker tiers (99% / 97%) keep
        // counting Glucose / HbA1c / TG/HDL only — the insulin signal feeds
        // into 95% / 85% / 80% / 60% paths.
        const insulinLow = hasInsulin && ba.insulin.isStricklyLessThanOptimal();
        const cpLow      = hasCp && ba.cpeptide.isStricklyLessThanOptimal();
        const isAlarmHigh = geAlarmHigh(ba.insulin) || geAlarmHigh(ba.cpeptide);
        const isAlarmLow  = leAlarmLow(ba.insulin)  || leAlarmLow(ba.cpeptide);
        const isLabHigh = (hasInsulin && ba.insulin.isGreaterThanLab()) ||
                          (hasCp && ba.cpeptide.isGreaterThanLab());
        const isLabLow  = (hasInsulin && ba.insulin.isLessThanLab()) ||
                          (hasCp && ba.cpeptide.isLessThanLab());
        const isOptHigh = (hasInsulin && ba.insulin.isGreaterThanOptimal()) ||
                          (hasCp && ba.cpeptide.isGreaterThanOptimal());

        // How far past optimal each off-optimal marker sits, graded at the
        // lab↔optimal midpoint — the same midpoint the immune gauge uses
        // (avgMax = (optimalMax + labMax) / 2, avgMin = (optimalMin + labMin) / 2).
        // "past midpoint" = more than half-way from optimal toward the lab bound
        // (isGreaterThanAvgMax high / isLessThanAvgMin low); otherwise the marker
        // is only mildly off-optimal. This lets the above-optimal pair and single
        // tiers report a lower likelihood when the markers barely clear optimal
        // instead of a flat value regardless of magnitude.
        const gPastMid = hasGlucose && ba.glucose.isGreaterThanAvgMax();
        const hPastMid = hasHbA1c && ba.hemoglobinA1c.isGreaterThanAvgMax();
        const tPastMid = hasTgHdl && ba.triglyceridesHDLRatio.isGreaterThanAvgMax();
        const isPastMidHigh = (hasInsulin && ba.insulin.isGreaterThanAvgMax()) ||
                              (hasCp && ba.cpeptide.isGreaterThanAvgMax());
        const gLowPastMid = hasGlucose && ba.glucose.isLessThanAvgMin();
        const hLowPastMid = hasHbA1c && ba.hemoglobinA1c.isLessThanAvgMin();
        const insulinLowPastMid = hasInsulin && ba.insulin.isLessThanAvgMin();
        const cpLowPastMid = hasCp && ba.cpeptide.isLessThanAvgMin();

        // Pair likelihood, graded by how many of the two markers clear the
        // midpoint: 80% both past, 72% one past, 65% both only mildly off. Each
        // step routes to its OWN Interpretation category so the DB title's %
        // always matches the gauge (id80 keeps the original pattern id; id72 /
        // id65 are the graded siblings 71040-71049).
        const pairDriver = (
            a: boolean, b: boolean,
            id80: PossibleRootCauseCategory, id72: PossibleRootCauseCategory, id65: PossibleRootCauseCategory,
        ) => {
            const n = (a ? 1 : 0) + (b ? 1 : 0);
            if (n === 2) return this.setGaugeDriver(80, id80);
            if (n === 1) return this.setGaugeDriver(72, id72);
            return this.setGaugeDriver(65, id65);
        };
        // Single likelihood: 60% past the midpoint (original id), 50% only
        // mildly off-optimal (graded sibling 71050-71057).
        const singleDriver = (pastMid: boolean, id60: PossibleRootCauseCategory, id50: PossibleRootCauseCategory) =>
            pastMid ? this.setGaugeDriver(60, id60) : this.setGaugeDriver(50, id50);

        const highLabCount = (gLab ? 1 : 0) + (hLab ? 1 : 0) + (tLab ? 1 : 0);

        // First-match-wins ladder. Within a tier, lab-range singles outrank
        // above-optimal pairs, and the high side is evaluated before the low
        // side (mixed high+low cases land on the high path — they're rare and
        // both directions show up in the underlying clinical patterns
        // separately).

        // ── 99% Highly Likely ─────────────────────────────────────────────
        if (highLabCount === 3) {
            return this.setGaugeDriver(99, PossibleRootCauseCategory.CATEGORY_71099);
        }
        if (gLowLab && hLowLab) {
            return this.setGaugeDriver(99, PossibleRootCauseCategory.CATEGORY_71098);
        }

        // ── 97% Highly Likely — 2 of 3 above lab ──────────────────────────
        if (highLabCount === 2) {
            if (gLab && hLab) return this.setGaugeDriver(97, PossibleRootCauseCategory.CATEGORY_71097);
            if (gLab && tLab) return this.setGaugeDriver(97, PossibleRootCauseCategory.CATEGORY_71096);
            /* hLab && tLab */ return this.setGaugeDriver(97, PossibleRootCauseCategory.CATEGORY_71095);
        }

        // ── 95% Highly Likely — single ≥ alarm ────────────────────────────
        // Per the product decision in the alarm-gate review: Glucose first
        // (a markedly elevated fasting glucose is the most concerning single
        // finding), then HbA1c, then TG/HDL, then the combined insulin-signal,
        // then the low-side mirror in the same order.
        if (gAlarm)     return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71094);
        if (hAlarm)     return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71093);
        if (tAlarm)     return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71092);
        if (isAlarmHigh) return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71082);
        if (gAlarmLow)  return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71091);
        if (hAlarmLow)  return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71090);
        if (isAlarmLow) return this.setGaugeDriver(95, PossibleRootCauseCategory.CATEGORY_71081);

        // ── 85% Likely — single > lab but < alarm ─────────────────────────
        if (gLab)    return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71089);
        if (hLab)    return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71088);
        if (isLabHigh) return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71080);
        if (gLowLab) return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71087);
        if (hLowLab) return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71086);
        if (isLabLow) return this.setGaugeDriver(85, PossibleRootCauseCategory.CATEGORY_71079);

        // ── 80% / 72% / 65% — above-optimal pair, graded by midpoint ───────
        // Flat 80% (Likely) when both markers clear the lab↔optimal midpoint;
        // steps down to 72% (Likely) when only one does and 65% (Possible) when
        // both are only mildly above optimal — the early-warning case where the
        // pattern is real but the markers barely leave the optimal band.
        // 71068 (Glucose + insulin-signal) and 71067 (HbA1c + insulin-signal)
        // catch the early-warning insulin-resistance pattern: pancreas is
        // already compensating but Glucose / HbA1c still in lab range.
        if (gHigh && hHigh) return pairDriver(gPastMid, hPastMid, PossibleRootCauseCategory.CATEGORY_71085, PossibleRootCauseCategory.CATEGORY_71040, PossibleRootCauseCategory.CATEGORY_71045);
        if (gHigh && tHigh) return pairDriver(gPastMid, tPastMid, PossibleRootCauseCategory.CATEGORY_71084, PossibleRootCauseCategory.CATEGORY_71041, PossibleRootCauseCategory.CATEGORY_71046);
        if (hHigh && tHigh) return pairDriver(hPastMid, tPastMid, PossibleRootCauseCategory.CATEGORY_71083, PossibleRootCauseCategory.CATEGORY_71042, PossibleRootCauseCategory.CATEGORY_71047);
        if (gHigh && isOptHigh) return pairDriver(gPastMid, isPastMidHigh, PossibleRootCauseCategory.CATEGORY_71068, PossibleRootCauseCategory.CATEGORY_71043, PossibleRootCauseCategory.CATEGORY_71048);
        if (hHigh && isOptHigh) return pairDriver(hPastMid, isPastMidHigh, PossibleRootCauseCategory.CATEGORY_71067, PossibleRootCauseCategory.CATEGORY_71044, PossibleRootCauseCategory.CATEGORY_71049);

        // ── 60% / 50% Possible — single above/below optimal, graded by midpoint
        // Past the lab↔optimal midpoint keeps 60%; only mildly off-optimal
        // steps down to 50% (both still Possible). Low-side singles grade at the
        // low midpoint (avgMin) the same way the high side grades at avgMax.
        if (gHigh)       return singleDriver(gPastMid, PossibleRootCauseCategory.CATEGORY_71066, PossibleRootCauseCategory.CATEGORY_71050);
        if (hHigh)       return singleDriver(hPastMid, PossibleRootCauseCategory.CATEGORY_71065, PossibleRootCauseCategory.CATEGORY_71051);
        if (tHigh)       return singleDriver(tPastMid, PossibleRootCauseCategory.CATEGORY_71064, PossibleRootCauseCategory.CATEGORY_71052);
        if (isOptHigh)   return singleDriver(isPastMidHigh, PossibleRootCauseCategory.CATEGORY_71059, PossibleRootCauseCategory.CATEGORY_71053);
        if (gLow)        return singleDriver(gLowPastMid, PossibleRootCauseCategory.CATEGORY_71063, PossibleRootCauseCategory.CATEGORY_71054);
        if (hLow)        return singleDriver(hLowPastMid, PossibleRootCauseCategory.CATEGORY_71062, PossibleRootCauseCategory.CATEGORY_71055);
        if (insulinLow)  return singleDriver(insulinLowPastMid, PossibleRootCauseCategory.CATEGORY_71061, PossibleRootCauseCategory.CATEGORY_71056);
        if (cpLow)       return singleDriver(cpLowPastMid, PossibleRootCauseCategory.CATEGORY_71060, PossibleRootCauseCategory.CATEGORY_71057);

        // ── Less Likely tier — confidence ladder ──────────────────────────
        // By the time we reach this point every measured marker is in its
        // optimal range (the 60% / 80% / 85% / 95% / 99% tiers would have
        // fired otherwise). The likelihood now scales with how much primary
        // evidence the practitioner has gathered. Insulin and C-peptide
        // optimal don't push the confidence lower per the product decision —
        // they only show up when no primary marker is measured at all.
        const gtCount = (hasGlucose ? 1 : 0) + (hasHbA1c ? 1 : 0) + (hasTgHdl ? 1 : 0);
        if (gtCount === 3) {
            return this.setGaugeDriver(5, PossibleRootCauseCategory.CATEGORY_71005);
        }
        if (gtCount === 2) {
            if (hasGlucose && hasHbA1c) return this.setGaugeDriver(15, PossibleRootCauseCategory.CATEGORY_71015);
            if (hasGlucose && hasTgHdl) return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71021);
            /* hasHbA1c && hasTgHdl */  return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71022);
        }
        if (gtCount === 1) {
            if (hasTgHdl)   return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71025);
            if (hasHbA1c)   return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71024);
            /* hasGlucose */ return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71023);
        }
        // No primary marker measured — Insulin or C-peptide must be the one
        // present (the early-return guard above requires at least one of the
        // five markers).
        return this.setGaugeDriver(25, PossibleRootCauseCategory.CATEGORY_71020);
    }

    private setGaugeDriver(probability: number, cat: PossibleRootCauseCategory) {
        this.glucoseRegulationProbability = probability;
        this.possibleRootCauseCategories.push(cat);
    }

    calculateGlucoseRegulationLikelihoodCategory() {
        const p = this.glucoseRegulationProbability;
        if (p === undefined || p < 0) return;
        if (p >= 90) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.HIGHLY_LIKELY;
        } else if (p >= 70) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.LIKELY;
        } else if (p >= 50) {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.POSSIBLE;
        } else {
            this.glucoseRegulationProbabilityCategory = ProbabilityCategory.LESS_LIKELY;
        }
    }

    private addGlucosePatternCategory(category: PossibleRootCauseCategory) {
        if (!this.possibleRootCauseCategories.includes(category)) {
            this.possibleRootCauseCategories.push(category);
        }
    }

    private markerHasValue(marker?: any): boolean {
        return !!marker && !!marker.hasValue && marker.hasValue();
    }

    private isMarkerHigh(marker?: any): boolean {
        if (!this.markerHasValue(marker)) return false;
        return marker.isGreaterThanLab?.() || marker.isGreaterThanOptimal?.() || false;
    }

    private isMarkerLow(marker?: any): boolean {
        if (!this.markerHasValue(marker)) return false;
        return marker.isLessThanLab?.() || marker.isStricklyLessThanOptimal?.() || marker.isLessThanOptimal?.() || false;
    }

    private isMarkerOptimal(marker?: any): boolean {
        return this.markerHasValue(marker) && !this.isMarkerHigh(marker) && !this.isMarkerLow(marker);
    }

    calculateInsights() {
        const ba = this.ba;

        // ── Marker presence ────────────────────────────────────────────────────
        const hasGlucose = this.markerHasValue(ba.glucose);
        const hasHbA1c   = this.markerHasValue(ba.hemoglobinA1c);
        const hasInsulin = this.markerHasValue(ba.insulin);
        const hasCp      = this.markerHasValue(ba.cpeptide);

        // ── Glucose / HbA1c states ─────────────────────────────────────────────
        const glucoseHigh = hasGlucose && this.isMarkerHigh(ba.glucose);
        const glucoseLow  = hasGlucose && this.isMarkerLow(ba.glucose);
        const glucoseOpt  = hasGlucose && this.isMarkerOptimal(ba.glucose);
        const hbA1cHigh   = hasHbA1c  && this.isMarkerHigh(ba.hemoglobinA1c);

        // ── Insulin / C-peptide states ─────────────────────────────────────────
        const insulinHigh = hasInsulin && this.isMarkerHigh(ba.insulin);
        const insulinLow  = hasInsulin && this.isMarkerLow(ba.insulin);
        const insulinOpt  = hasInsulin && this.isMarkerOptimal(ba.insulin);
        const cpHigh      = hasCp     && this.isMarkerHigh(ba.cpeptide);
        const cpLow       = hasCp     && this.isMarkerLow(ba.cpeptide);
        const cpOpt       = hasCp     && this.isMarkerOptimal(ba.cpeptide);

        // Discrepant insulin / C-peptide (one ↑, the other ↓). Physiologically
        // co-secreted, so this combination is exceedingly rare (~1 in 15k tests)
        // and typically indicates lab error. When detected, primary patterns
        // are routed as if BOTH insulin and C-peptide were absent, and a
        // single add-on (22.x / CATEGORY_73250) renders below.
        const mixedInsulinCpDiscrepancy =
            (insulinHigh && cpLow) || (cpHigh && insulinLow);

        // ── Lipid offset: 0 = none, 50 = TG/HDL↑, 100 = TG↑+TG/HDL not high ──
        const tgHdlHigh  = this.markerHasValue(ba.triglyceridesHDLRatio) && this.isMarkerHigh(ba.triglyceridesHDLRatio);
        const tgHigh     = this.markerHasValue(ba.triglycerides) && this.isMarkerHigh(ba.triglycerides);
        const lipidOffset = tgHdlHigh ? 50 : (tgHigh ? 100 : 0);

        // ── Insulin variant (0–45, step 5) for main high-glucose patterns ──────
        // When insulin and C-peptide disagree (one ↑, the other ↓), treat
        // both as absent for routing — the 22.x add-on (CATEGORY_73250)
        // flags the discrepancy separately below.
        const insulinVariant = (() => {
            if (mixedInsulinCpDiscrepancy) return 0;
            if (!hasInsulin && !hasCp) return 0;
            if (hasInsulin && !hasCp) {
                if (insulinHigh) return 5;
                if (insulinOpt)  return 20;
                return 35; // insulinLow
            }
            if (!hasInsulin && hasCp) {
                if (cpHigh) return 10;
                if (cpOpt)  return 25;
                return 40; // cpLow
            }
            // Both present.
            // high takes priority, then both-low, then single-low
            // (the opt counterpart is treated as silent), then both-opt.
            if (insulinHigh || cpHigh) return 15;
            if (insulinLow  && cpLow)  return 45;
            if (insulinLow)            return 35; // insulin↓, cp = opt
            if (cpLow)                 return 40; // cp↓, insulin = opt
            return 30; // both optimal
        })();

        // ── Main pattern: groupBase + lipidOffset + insulinVariant ─────────────
        if ((glucoseHigh || hbA1cHigh) && insulinVariant >= 0) {
            let groupBase: number;
            if      (glucoseHigh && hbA1cHigh)    groupBase = 72800;
            else if (glucoseHigh && !hasHbA1c)    groupBase = 72000;
            else if (glucoseHigh)                  groupBase = 72400; // HbA1c present but ≤ optimal
            else if (hbA1cHigh && !hasGlucose)    groupBase = 72200;
            else                                   groupBase = 72600; // Glucose present but ≤ optimal
            const catId = groupBase + lipidOffset + insulinVariant;
            this.addGlucosePatternCategory((PossibleRootCauseCategory as any)['CATEGORY_' + catId]);
        }

        // ── Low glucose primary patterns (19.1, 19.3–19.8: 73060, 73070–73083) ─
        // ── plus lipid variants (19.9–19.22: 73084–73097) ──────────────────────
        // Each pattern's conditions are mutually exclusive via insulin/cp
        // presence and lipid state, so only one fires per bloodwork.
        // "≤ Opt" in sheet = has value AND not high. "blank" = !hasValue.
        // Lipid groups: TG/HDL high → 73084 base; TG high (TG/HDL ≤ opt) → 73091 base.
        if (glucoseLow && !hbA1cHigh) {
            const glucoseBelowLab = hasGlucose && (ba.glucose as any).isLessThanLab?.() === true;
            const insulinLeOpt = hasInsulin && !insulinHigh; // Low or Optimal
            const cpLeOpt      = hasCp      && !cpHigh;

            const tgLow      = tgHigh && !tgHdlHigh;
            const lipidBase  = tgHdlHigh ? 73084 : (tgLow ? 73091 : null);

            // configOffset selects the insulin/cp variant within each lipid
            // sub-family. Same ordering as the non-lipid 19.x parents.
            //   0 = no insulin/cp, 1 = insulin↑, 2 = cp↑, 3 = both↑,
            //   4 = insulin≤opt, 5 = cp≤opt, 6 = both≤opt
            const configOffset = (() => {
                if (!hasInsulin && !hasCp) return 0;
                if (insulinHigh && !hasCp) return 1;
                if (!hasInsulin && cpHigh) return 2;
                if (insulinHigh && cpHigh) return 3;
                if (insulinLeOpt && !hasCp) return 4;
                if (!hasInsulin && cpLeOpt) return 5;
                if (insulinLeOpt && cpLeOpt) return 6;
                return -1; // mixed (e.g. insulin↑ + cp≤opt) has no sheet pattern
            })();
            const nonLipidIds = [73060, 73070, 73075, 73080, 73081, 73082, 73083];

            if (configOffset >= 0) {
                const categoryId = lipidBase !== null
                    ? lipidBase + configOffset
                    : nonLipidIds[configOffset];
                this.addGlucosePatternCategory(
                    (PossibleRootCauseCategory as any)['CATEGORY_' + categoryId],
                );
            }

            // 19.2 — "Additional Evaluation Warranted" severity add-on.
            // Fires whenever glucose is below lab range, regardless of
            // insulin/cp state. Emitted LAST so it renders below the more
            // specific pattern in the UI.
            if (glucoseBelowLab) {
                this.addGlucosePatternCategory(
                    (PossibleRootCauseCategory as any)['CATEGORY_73065'],
                );
            }
        }

        // ── Optimal glucose + high insulin/cp (21.x: 73200–73240) ─────────────
        // Skip when insulin/cp are discrepant — they're treated as absent for
        // routing, so the "high insulin/cp" gate doesn't apply. The 22.x
        // add-on (CATEGORY_73250) flags the discrepancy below.
        if (glucoseOpt && !hbA1cHigh && (insulinHigh || cpHigh) && !mixedInsulinCpDiscrepancy) {
            const lipidOffset21    = tgHdlHigh ? 15 : (tgHigh ? 30 : 0);
            const insulinVariant21 = (insulinHigh && cpHigh) ? 10 : (insulinHigh ? 0 : 5);
            this.addGlucosePatternCategory((PossibleRootCauseCategory as any)['CATEGORY_' + (73200 + lipidOffset21 + insulinVariant21)]);
        }

        // ── Add-ons (fire independently of primary pattern) ───────────────────
        const anyHigh = glucoseHigh || hbA1cHigh;

        // 16.1: Elevated LDL Compounding Glucose-Related Cardiometabolic Risk
        if (anyHigh && this.isMarkerHigh(ba.ldl)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73000);
        }

        // 17.1: Liver Stress May Be Contributing to Glucose Dysregulation
        if (anyHigh && this.isMarkerHigh(ba.alt)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73010);
        }

        // 18.1: Stress Hormone Influence (Cortisol AM high)
        if (anyHigh && this.isMarkerHigh(ba.cortisolAM)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73020);
        }

        // 18.2: Acute Stress Pattern (NLR high + hs-CRP optimal or no value)
        if (anyHigh && this.isMarkerHigh(ba.nlr) && !this.isMarkerHigh(ba.hsCRP) && !this.isMarkerLow(ba.hsCRP)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73025);
        }

        // 18.3: Protein Metabolism Influence (CO2 low + BUN high)
        if (anyHigh && this.isMarkerLow(ba.carbonDioxide) && this.isMarkerHigh(ba.bun)) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73030);
        }

        // 18.4–18.6: Inadequate Pancreatic Response — insulin & C-peptide low,
        // K and/or Ca strictly below lab range. K+Ca variant takes precedence.
        if (anyHigh && insulinLow && cpLow) {
            const kLabLow  = !!ba.potassium?.isLessThanLab?.();
            const caLabLow = !!ba.calcium?.isLessThanLab?.();
            if      (kLabLow && caLabLow) this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73045);
            else if (kLabLow)             this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73035);
            else if (caLabLow)            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73040);
        }

        // 18.7–18.10: Low Amylase pancreatic context — amylase low with elevated
        // glucose or HbA1c. Combined low insulin + low C-peptide variant (18.10)
        // takes precedence over the single-marker variants (18.8 / 18.9),
        // which take precedence over the amylase-only base pattern (18.7).
        if (anyHigh && this.isMarkerLow(ba.amylase)) {
            if      (insulinLow && cpLow) this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73059);
            else if (insulinLow)          this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73055);
            else if (cpLow)               this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73057);
            else                          this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73050);
        }

        // 20.1: Protein-Influenced Low Glucose
        if (glucoseLow && this.isMarkerHigh(ba.bun) &&
            (!this.markerHasValue(ba.creatinine) || !this.isMarkerHigh(ba.creatinine))) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73100);
        }

        // 20.2: Low Cortisol-Influenced Glucose Pattern
        if (glucoseLow && (this.isMarkerLow(ba.cortisolAM) || this.isMarkerLow(ba.cortisolPM))) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73105);
        }

        // 20.3–20.6: B6 / ALT / AST Low Glucose Add-ons
        if (glucoseLow) {
            const b6Low  = this.markerHasValue(ba.vitaminB6) && this.isMarkerLow(ba.vitaminB6);
            const altLow = this.isMarkerLow(ba.alt);
            const astLow = this.isMarkerLow(ba.ast);
            if      (b6Low)            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73110);
            else if (altLow && astLow) this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73125);
            else if (altLow)           this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73115);
            else if (astLow)           this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73120);
        }

        // 22.1: Insulin / C-peptide Discrepancy add-on. Fires whenever the
        // two markers disagree (one ↑, the other ↓) regardless of glucose
        // state. Both markers were already treated as absent in primary-
        // pattern routing above; this add-on surfaces the discrepancy so the
        // practitioner can investigate (retest, lab error, unusual physiology).
        if (mixedInsulinCpDiscrepancy) {
            this.addGlucosePatternCategory(PossibleRootCauseCategory.CATEGORY_73250);
        }

        // ── Existing single-marker categories ─────────────────────────────────
        if (this.ba.glucose.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6000);
        } else if (this.ba.glucose.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6010);
        }

        if (this.ba.hemoglobinA1c.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6020);
        } else if (this.ba.hemoglobinA1c.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6030);
        }

        if (this.ba.insulin.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6040);
        } else if (this.ba.insulin.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6050);
        }

        if (this.ba.cpeptide.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6060);
        } else if (this.ba.cpeptide.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6070);
        }

        if (
          this.ba.triglycerides.isGreaterThanOptimal() &&
          this.ba.triglyceridesHDLRatio.isLessThanOrIsOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6075,
          );
        } else if (
          this.ba.triglycerides.isGreaterThanOptimal() &&
          this.ba.triglyceridesHDLRatio.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6076,
          );
        } else if (
          this.ba.triglycerides.isLessThanOrIsOptimal() &&
          this.ba.triglyceridesHDLRatio.isGreaterThanOptimal()
        ) {
          this.possibleRootCauseCategories.push(
            PossibleRootCauseCategory.CATEGORY_6077,
          );
        }

        if (this.ba.amylase.isGreaterThanOptimal()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6080);
        }

        if (this.ba.amylase.isGreaterThanLab()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6085);
        }

        if (this.ba.amylase.isLessThanOptimal()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6090);
        }

        if (this.ba.amylase.isLessThanLab()) {
        this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_6091);
        }

    }
}